import { useWindowSize } from "@react-hookz/web";
import * as Sentry from "@sentry/browser";
import {
  CallRingingHandler,
  Content,
  type OnRinging,
  useUpscopeConnection,
  useUpscopeViewer,
  VideoFrame,
  VideoProvider,
  ViewerProvider,
} from "@upscopeio/viewer-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useTitle } from "react-use";

import DigitAccess from "./components/misc/DigitAccess";
import Button from "./components/ui/Button";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import Well from "./components/ui/Well";
import { ApiResponses, Unsuccessful } from "./data/api-endpoints";
import t, { initI18n } from "./i18n";
import logger, { setLoggerContext } from "./log";
import ApiCacheProvider from "./providers/ApiCacheProvider";
import DarkModeProvider from "./providers/DarkModeProvider";
import { ModalProvider, useModal } from "./providers/ModalProvider";
import { useApiData } from "./use/api";

const subdomain = location.hostname.split(".")[0]!;
const accessCode = location.pathname.slice(1) || null;
const canRotate =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  localStorage.getItem("rotate_test") === "true";

if (accessCode && !accessCode.match(/^\d+$/)) location.href = "/";

if (process.env.NODE_ENV !== "development" && process.env.NODE_ENV !== "onprem") {
  const errorLogger = logger("app:errors");
  setLoggerContext({ accessCode, subdomain });

  Sentry.init({
    beforeSend: (event, hint) => {
      const error = hint.originalException ?? hint.syntheticException ?? event.message;
      if (error) errorLogger("%O", error);
      return event;
    },
    dsn: "https://7a39720e837da4dd86d7488c4b521aef@o73137.ingest.us.sentry.io/4512263967997952",
    environment: process.env.NODE_ENV,
    maxValueLength: 2500,
    release: `helloscreen-frontend@${process.env.CURRENT_APP_VERSION}`,
  });

  const loggersByNamespace: Record<string, ReturnType<typeof logger>> = {};

  window.__log = (level, namespace, message, ...args) => {
    loggersByNamespace[namespace] ??= logger(`helloscreen:publicLink:${namespace}`);
    switch (level) {
      case "info":
        loggersByNamespace[namespace](message, ...args);
        break;
      case "warn":
        loggersByNamespace[namespace].warning(message, ...args);
        break;
    }
  };
}

window.reload = () => {
  sessionStorage.setItem("reload", "true");
  location.reload();
};

type RotationState = "normal" | "offered" | "pending" | "rotated";

function PublicLinkAccess({ accessCode }: { accessCode: null | string }) {
  useTitle("HelloScreen");
  const [error, setError] = useState<Unsuccessful<ApiResponses["hs.publicLink.sessions.create"]>["error"]>();
  const [hasEnded, setHasEnded] = useState(false);
  const { endpoint, isLoading, region, shortId, token } = useApiData(
    "hs.publicLink.sessions.create",
    { accessCode, subdomain },
    {
      onError: ({ error }) => {
        setError(error);
      },
      refreshStale: false,
    },
  );

  const onAccessCode = useCallback((code: string) => {
    location.pathname = `/${code}`;
  }, []);

  if (error === "access_code_needed") return <AccessCode notFound={false} onAccessCode={onAccessCode} />;
  if (error === "invalid_access_code") return <AccessCode notFound={true} onAccessCode={onAccessCode} />;
  if (error === "invalid_subdomain") {
    return (
      <Well>
        <Well.Title>{t("Incorrect link")}</Well.Title>
        <p>{t("The link you entered does not look correct. Please check and try again.")}</p>
      </Well>
    );
  }
  if (error === "user_not_online") {
    return (
      <Well>
        <Well.Title>{t("Not online")}</Well.Title>
        <p>{t("The link you entered does not have an active presentation. Please try again later.")}</p>
        <Well.Footer>
          <Button
            onClick={() => {
              window.reload();
            }}
            variant="action"
          >
            {t("Try again")}
          </Button>
        </Well.Footer>
      </Well>
    );
  }
  undefined satisfies typeof error;

  if (isLoading) return <LoadingSpinner />;
  if (hasEnded) return <RenderOnEnded />;
  return (
    <ViewerProvider endpoint={endpoint} idleDisconnectSeconds={null} region={region} shortId={shortId} token={token}>
      <VideoProvider i18n={{ requestVideo: t("Request video") }}>
        <PublicLinkViewer setHasEnded={setHasEnded} />
      </VideoProvider>
    </ViewerProvider>
  );
}

function PublicLinkViewer({ setHasEnded }: { setHasEnded: (ended: boolean) => void }) {
  const { height, width } = useWindowSize();
  const { connected, listen, loadingStagesLeft, sendCustomMessage, sessionStatus } = useUpscopeViewer();
  const { size } = useUpscopeConnection();
  const { height: divHeight, width: divWidth } = size || { height: 0, width: 0 };
  const { openModal } = useModal();

  const onVideoRinging = useCallback<OnRinging>(
    (accept) => {
      return openModal({
        cancelText: t("Refuse"),
        confirmText: t("Accept"),
        content: t("The presenter wants to start a video call. Do you want to accept?"),
        onClick: (option) => {
          accept(option);
        },
        title: t("Incoming video call"),
        type: "info",
      });
    },
    [openModal],
  );

  const viewerPortrait = height > width;
  const contentLandscape = divWidth > divHeight;
  const hasValidSize = divWidth > 0 && divHeight > 0;

  const [rotationState, setRotationState] = useState<RotationState>("pending");
  const [allLandscape, setAllLandscape] = useState<boolean | undefined>(undefined);
  const shouldRotate = rotationState === "rotated";

  // Send offer when conditions are first met
  const offerSentRef = useRef(false);
  useEffect(() => {
    if (!hasValidSize || offerSentRef.current) return;
    if (!canRotate || !viewerPortrait || !contentLandscape) {
      setRotationState("normal");
      return;
    }
    offerSentRef.current = true;
    setRotationState("offered");
    sendCustomMessage({ state: "offer", type: "rotateState" });
  }, [hasValidSize, viewerPortrait, contentLandscape, sendCustomMessage]);

  // Listen for allLandscape broadcast from sharer
  useEffect(() => {
    if (!listen) return;
    listen("customMessage", ([_from, message]) => {
      if (typeof message.allLandscape === "boolean") setAllLandscape(message.allLandscape);
    });
  }, [listen]);

  // Rotate when allLandscape=true AND we offered
  useEffect(() => {
    if (rotationState !== "offered" || allLandscape !== true) return;
    setRotationState("rotated");
    sendCustomMessage({ state: "rotated", type: "rotateState" });
  }, [rotationState, allLandscape, sendCustomMessage]);

  // Unrotate if user physically rotates device to landscape
  useEffect(() => {
    if (rotationState !== "rotated" || viewerPortrait) return;
    setRotationState("normal");
    sendCustomMessage({ state: "normal", type: "rotateState" });
  }, [rotationState, viewerPortrait, sendCustomMessage]);

  useEffect(() => {
    if (!connected || sessionStatus) return;
    setHasEnded(true);
  }, [connected, sessionStatus, setHasEnded]);

  return (
    <>
      {loadingStagesLeft.length > 0 && <LoadingSpinner />}
      <CallRingingHandler onVideoRinging={onVideoRinging} />
      <VideoFrame />
      <div
        className="shadow-multi overflow-hidden rounded dark:border dark:border-slate-700"
        style={{
          height: `${shouldRotate ? divWidth : divHeight}px`,
          opacity: loadingStagesLeft.length > 0 ? 0 : 1,
          position: loadingStagesLeft.length > 0 ? "absolute" : "relative",
          width: `${shouldRotate ? divHeight : divWidth}px`,
        }}
      >
        <div
          className="origin-top-right transition-transform"
          style={
            shouldRotate
              ? {
                  left: "100%",
                  position: "absolute",
                  top: "0",
                  transform: "rotate(90deg) translate(-50%, -50%)",
                }
              : undefined
          }
        >
          <Content
            activeConnectionId={undefined}
            color="red"
            controlTool="cursor"
            maxHeight={(shouldRotate ? width : height) - 20}
            maxWidth={(shouldRotate ? height : width) - 20}
          />
        </div>
      </div>
    </>
  );
}

void initI18n(navigator.language).then(() => {
  const root = createRoot(document.querySelector("#root")!);

  root.render(
    <ApiCacheProvider>
      <DarkModeProvider>
        <ModalProvider>
          <div className="flex h-screen w-full flex-1 touch-none select-none items-center justify-center text-center">
            <PublicLinkAccess accessCode={accessCode} />
          </div>
        </ModalProvider>
      </DarkModeProvider>
    </ApiCacheProvider>,
  );
});

function AccessCode({ notFound, onAccessCode }: { notFound: boolean; onAccessCode: (code: string) => void }) {
  return (
    <DigitAccess
      onChange={onAccessCode}
      subTitle={
        notFound
          ? t("Unable to find a session. Please, enter a session code to start a new session.")
          : t("Please enter the code the agent gives you to get started.")
      }
      title={notFound ? t("Oops! Session Not Found!") : t("Please enter access code")}
    />
  );
}

function RenderOnEnded() {
  useEffect(() => {
    window.close();
  }, []);
  return (
    <Well>
      <Well.Title>{t("The session has now ended!")}</Well.Title>
      <p>{t("Thank you for using HelloScreen.")}</p>
    </Well>
  );
}
